import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем токен к каждому запросу
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Просто пробрасываем ошибки — каждый компонент обрабатывает сам.
// checkAuth в App.jsx логаутит при 401, фоновые запросы глотают ошибки.
// Это предотвращает ложный выброс из аккаунта при рестарте сервиса.
apiClient.interceptors.response.use(
  res => res,
  err => Promise.reject(err)
);

// Auth API
export const authAPI = {
  googleAuth: (credential) =>
    apiClient.post('/auth/google', { credential }),

  getMe: () =>
    apiClient.get('/auth/me'),

  // Admin
  heartbeat: () =>
    apiClient.post('/auth/heartbeat'),

  logout: () =>
    apiClient.post('/auth/logout'),

  getOnline: () =>
    apiClient.get('/auth/admin/online'),

  getUsers: () =>
    apiClient.get('/auth/admin/users'),

  approveUser: (id) =>
    apiClient.post(`/auth/admin/users/${id}/approve`),

  rejectUser: (id) =>
    apiClient.post(`/auth/admin/users/${id}/reject`),

  inviteUser: (email) =>
    apiClient.post('/auth/admin/users/invite', { email }),

  updateUser: (id, data) =>
    apiClient.patch(`/auth/admin/users/${id}`, data),

  deleteUser: (id) =>
    apiClient.delete(`/auth/admin/users/${id}`),

  // Права
  getPermissionsList: () =>
    apiClient.get('/auth/admin/permissions/list'),
  getUserPermissions: (id) =>
    apiClient.get(`/auth/admin/users/${id}/permissions`),
  setUserPermissions: (id, permissions) =>
    apiClient.put(`/auth/admin/users/${id}/permissions`, { permissions }),

  // Импersonация
  startImpersonate: (email) =>
    apiClient.post('/auth/admin/impersonate', { email }),
  stopImpersonate: () =>
    apiClient.post('/auth/admin/impersonate/stop'),
  getImpersonateStatus: () =>
    apiClient.get('/auth/admin/impersonate/status'),
};

// Products API
export const productsAPI = {
  getBrandsModels: (search = '', sizes = [], allSizes = false, withSizes = false, minSupplierPrice = '', maxSupplierPrice = '', newItems = false, season = '') => {
    const params = {};
    if (search) params.search = search;
    if (season) params.season = season;
    if (sizes && sizes.length > 0) params.sizes = sizes.join(',');
    if (allSizes) params.all_sizes = true;
    if (withSizes) params.with_sizes = true;
    if (minSupplierPrice) params.min_supplier_price = minSupplierPrice;
    if (maxSupplierPrice) params.max_supplier_price = maxSupplierPrice;
    if (newItems) params.new_items = true;
    return apiClient.get('/products/brands-models', { params });
  },

  getSizes: () =>
    apiClient.get('/products/sizes'),

  listProducts: (skip = 0, limit = 10, filters = {}, signal) => {
    const { sizes, allSizes, withSizes, minSupplierPrice, maxSupplierPrice, newItems, ...rest } = filters;
    const params = { skip, limit, ...rest };
    if (sizes && sizes.length > 0) params.sizes = sizes.join(',');
    if (allSizes) params.all_sizes = true;
    if (withSizes) params.with_sizes = true;
    if (minSupplierPrice) params.min_supplier_price = minSupplierPrice;
    if (maxSupplierPrice) params.max_supplier_price = maxSupplierPrice;
    if (newItems) { params.new_items = true; params.limit = 100; params.skip = 0; }
    return apiClient.get('/products/', { params, ...(signal ? { signal } : {}) });
  },

  // Позиція товару в межах тих самих фільтрів/сортування, що й listProducts —
  // щоб відкрити сторінку, де товар реально лежить, без послідовного гортання.
  findProductPosition: (sku, filters = {}) => {
    const { sizes, allSizes, withSizes, minSupplierPrice, maxSupplierPrice, ...rest } = filters;
    const params = { sku, ...rest };
    if (sizes && sizes.length > 0) params.sizes = sizes.join(',');
    if (allSizes) params.all_sizes = true;
    if (withSizes) params.with_sizes = true;
    if (minSupplierPrice) params.min_supplier_price = minSupplierPrice;
    if (maxSupplierPrice) params.max_supplier_price = maxSupplierPrice;
    return apiClient.get('/products/find-position', { params });
  },

  getProduct: (productId) =>
    apiClient.get(`/products/${productId}`),

  getBySku: (sku) =>
    apiClient.get('/products/by-sku', { params: { sku } }),

  getSuppliers: (productId) =>
    apiClient.get(`/products/${productId}/suppliers`),

  syncProducts: () =>
    apiClient.post('/products/sync'),

  getSyncStatus: () =>
    apiClient.get('/products/sync/status'),

  syncSuppliers: () =>
    apiClient.post('/products/sync-suppliers'),

  getSuppliersStatus: () =>
    apiClient.get('/products/sync-suppliers/status'),

  syncAll: () =>
    apiClient.post('/products/sync-all'),
};

export const discountsAPI = {
  getSuppliers: () =>
    apiClient.get('/discounts/suppliers'),

  list: () =>
    apiClient.get('/discounts/'),

  create: (supplier_name_norm, discount_amount) =>
    apiClient.post('/discounts/', { supplier_name_norm, discount_amount }),

  update: (id, discount_amount) =>
    apiClient.patch(`/discounts/${id}`, { discount_amount }),

  remove: (id) =>
    apiClient.delete(`/discounts/${id}`),
};

export const adminItemsAPI = {
  generateProductName: (photo_url, hint_text) =>
    apiClient.post('/admin/generate-product-name', { photo_url, hint_text }),
  getQueue: () => apiClient.get('/admin/queue'),
  getCacheStatus: () => apiClient.get('/admin/cache-status'),
  refreshCache: () => apiClient.post('/admin/cache-refresh'),
  getSupplierQueue: (supplier, { refresh = false } = {}) =>
    apiClient.get(`/admin/queue/${encodeURIComponent(supplier)}`, { params: refresh ? { refresh: true } : {} }),
  getArticleDetails: (supplier, article, signal) =>
    apiClient.get('/admin/article-details', { params: { supplier, article }, ...(signal ? { signal } : {}) }),
  addItem: (supplier, supplier_article, catalog_id, photo_link, photo_url, keywords, is_new) =>
    apiClient.post('/admin/add-item', { supplier, supplier_article, catalog_id, photo_link, photo_url, keywords, is_new }),
  getActivity: (days = 30) =>
    apiClient.get('/admin/activity', { params: { days } }),
  uploadPhoto: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post('/admin/upload-photo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  prefetchSupplier: (supplier) =>
    apiClient.get('/admin/prefetch-supplier', { params: { supplier } }),

  rehostPhoto: (url) =>
    apiClient.get('/admin/rehost-photo', { params: { url } }),

  rotatePhoto: (url, degrees) => {
    const form = new FormData();
    form.append('url', url);
    form.append('degrees', String(degrees));
    return apiClient.post('/admin/rotate-photo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  revertPhoto: (url) => {
    const form = new FormData();
    form.append('url', url);
    return apiClient.post('/admin/revert-photo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  startScanImageSizes: () =>
    apiClient.post('/admin/scan-image-sizes'),
  getScanImageSizesStatus: () =>
    apiClient.get('/admin/scan-image-sizes/status'),
  getImageCatalog: (params) =>
    apiClient.get('/admin/image-catalog', { params }),
  rotatePhotosBulk: (urls, degrees) => {
    const form = new FormData();
    form.append('urls', JSON.stringify(urls));
    form.append('degrees', degrees);
    return apiClient.post('/admin/rotate-photos-bulk', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  markPhotosVerified: (urls, verified = true) => {
    const form = new FormData();
    form.append('urls', JSON.stringify(urls));
    form.append('verified', verified);
    return apiClient.post('/admin/mark-photos-verified', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  findSimilarArticles: (supplier, article) =>
    apiClient.get('/admin/article-similar', { params: { supplier, article } }),

  getEasybdpromUrl: (supplier, article) =>
    apiClient.get('/admin/article-easybdprom-url', { params: { supplier, article } }),

  proxySupplierPhoto: (url) =>
    apiClient.get('/admin/proxy-supplier-photo', { params: { url }, responseType: 'blob' }),

  renameArticle: (supplier, oldArticle, newArticle) =>
    apiClient.post('/admin/rename-article', { supplier, old_article: oldArticle, new_article: newArticle }),

  getNewItemConfig: () =>
    apiClient.get('/admin/new-item-config'),
  addNewCatalogItem: (payload) =>
    apiClient.post('/admin/add-new-catalog-item', payload),
  addCatalogModel: (payload) =>
    apiClient.post('/admin/add-catalog-model', payload),
  // DEBUG_TODO: remove after testing
  debugStep: (step, payload) =>
    apiClient.post('/admin/debug/step', { step, ...payload }),
  debugNewCatalogStep: (step, payload) =>
    apiClient.post('/admin/debug/new-catalog-step', { step, ...payload }),

  getClothingItemConfig: () =>
    apiClient.get('/admin/clothing-item-config'),
  addClothingItem: (payload) =>
    apiClient.post('/admin/add-clothing-item', payload),
  fixItemSheet: (payload) =>
    apiClient.post('/admin/fix-item-sheet', payload),
  runGasAvtoart: (supplier) =>
    apiClient.post('/admin/run-gas-avtoart', { supplier }),
};

export const jobsAPI = {
  enqueueSupplierItem: (payload) =>
    apiClient.post('/admin/jobs/supplier-item', payload),
  enqueueNewCatalogItem: (payload) =>
    apiClient.post('/admin/jobs/new-catalog-item', payload),
  enqueueRenameArticle: (supplier, oldArticle, newArticle) =>
    apiClient.post('/admin/jobs/rename-article', { supplier, old_article: oldArticle, new_article: newArticle }),
  enqueueClothingItem: (payload) =>
    apiClient.post('/admin/jobs/clothing-item', payload),
  enqueueCatalogModel: (payload) =>
    apiClient.post('/admin/jobs/catalog-model', payload),
  list: () =>
    apiClient.get('/admin/jobs'),
  retry: (jobId) =>
    apiClient.post(`/admin/jobs/${jobId}/retry`),
  cancel: (jobId) =>
    apiClient.delete(`/admin/jobs/${jobId}`),
};

export const configAPI = {
  getConfig: () =>
    apiClient.get('/auth/admin/config'),

  updateConfig: (key, value) =>
    apiClient.put(`/auth/admin/config/${key}`, { value }),

  getAuditLog: (params) =>
    apiClient.get('/auth/admin/audit-log', { params }),
};

export const manageAPI = {
  getStaleProducts: (months = 6, offset = 0) =>
    apiClient.get('/admin/manage/stale-products', { params: { months, offset, limit: 200 } }),
  getSuppliers: () =>
    apiClient.get('/admin/manage/suppliers'),
  getSupplierArticles: (supplier) =>
    apiClient.get('/admin/manage/supplier-articles', { params: { supplier } }),
  search: (q, limit = 20) =>
    apiClient.get('/admin/manage/search', { params: { q, limit } }),
  getProduct: (sku) =>
    apiClient.get(`/admin/manage/products/${encodeURIComponent(sku)}`),
  updateProduct: (sku, fields) =>
    apiClient.patch(`/admin/manage/products/${encodeURIComponent(sku)}`, fields),
  replacePhotoJob: (sku, photoUrl) =>
    apiClient.post(`/admin/manage/products/${encodeURIComponent(sku)}/replace-photo`, { photo_url: photoUrl }),
  editCatalogProduct: (sku, fields) =>
    apiClient.post(`/admin/manage/products/${encodeURIComponent(sku)}/edit`, fields),
  deleteProduct: (sku) =>
    apiClient.delete(`/admin/manage/products/${encodeURIComponent(sku)}`),
  deleteProductJob: (sku) =>
    apiClient.post(`/admin/manage/products/${encodeURIComponent(sku)}/delete-job`),
  merge: (primarySku, secondarySkus) =>
    apiClient.post('/admin/manage/merge', { primary_sku: primarySku, secondary_skus: secondarySkus }),
  mergeJob: (primarySku, secondarySkus) =>
    apiClient.post('/admin/manage/merge-job', { primary_sku: primarySku, secondary_skus: secondarySkus }),
  reassign: (supplier, supplierArticle, newCatalogId) =>
    apiClient.post('/admin/manage/reassign', { supplier, supplier_article: supplierArticle, new_catalog_id: newCatalogId }),
  reindexSuppliers: () => apiClient.post('/admin/manage/reindex-suppliers'),
  reindexSuppliersStatus: () => apiClient.get('/admin/manage/reindex-suppliers/status'),

  updateSupplierItem: (supplier, supplierArticle, fields) =>
    apiClient.post('/admin/manage/update-supplier-item', { supplier, supplier_article: supplierArticle, ...fields }),
};

export const alertsAPI = {
  create: (data) => apiClient.post('/alerts/restock', data),
  list: () => apiClient.get('/alerts/restock'),
  delete: (id) => apiClient.delete(`/alerts/restock/${id}`),
};

export const groupsAPI = {
  list: () => apiClient.get('/auth/admin/groups').then(r => r.data),
  create: (name) => apiClient.post('/auth/admin/groups', { name }).then(r => r.data),
  rename: (id, name) => apiClient.patch(`/auth/admin/groups/${id}`, { name }).then(r => r.data),
  delete: (id) => apiClient.delete(`/auth/admin/groups/${id}`).then(r => r.data),
  setActive: (id, is_active) => apiClient.post(`/auth/admin/groups/${id}/set-active`, { is_active }).then(r => r.data),
};

// Для <img src> прокси не нужен — браузер грузит любой URL напрямую.
// Прокси остался только для clipboard copy (fetch требует CORS).
export const proxyImageUrl = (url) => {
  if (!url) return '';
  if (url.includes('alldrop.ink')) return url;
  return `/api/v1/products/proxy-image?url=${encodeURIComponent(url)}`;
};

export const photoFixAPI = {
  getStats:         ()                  => apiClient.get('/admin/photo-fix/stats'),
  getMissing:       (category, params)  => apiClient.get(`/admin/photo-fix/missing`, { params: { category, ...params } }),
  syncCatalogSheet: (config) => apiClient.post('/admin/photo-fix/sync-catalog-sheet', undefined, config),
  syncSupplierSheets:       ()  => apiClient.post('/admin/photo-fix/sync-supplier-sheets'),
  syncSupplierSheetsStatus: ()  => apiClient.get('/admin/photo-fix/sync-supplier-sheets/status'),
  syncSupplierSheetsStop:   ()  => apiClient.post('/admin/photo-fix/sync-supplier-sheets/stop'),
  getSupplierSheets:        ()  => apiClient.get('/admin/photo-fix/supplier-sheets'),
  saveSupplierSheetsOrder:  (s) => apiClient.post('/admin/photo-fix/supplier-sheets/order', s),
  bulkDownload:             ()  => apiClient.post('/admin/photo-fix/bulk-download'),
  bulkDownloadStatus:       ()  => apiClient.get('/admin/photo-fix/bulk-download/status'),
  bulkDownloadStop:         ()  => apiClient.post('/admin/photo-fix/bulk-download/stop'),
  tgDownload:               ()  => apiClient.post('/admin/photo-fix/tg-download'),
  tgDownloadStatus:         ()  => apiClient.get('/admin/photo-fix/tg-download/status'),
  tgDownloadStop:           ()  => apiClient.post('/admin/photo-fix/tg-download/stop'),
  cleanupStubs:             ()  => apiClient.post('/admin/photo-fix/cleanup-stubs'),
  syncPriceSheets:          ()  => apiClient.post('/admin/photo-fix/sync-price-sheets'),
  syncPriceSheetsStatus:    ()  => apiClient.get('/admin/photo-fix/sync-price-sheets/status'),
  syncPriceSheetsStop:      ()  => apiClient.post('/admin/photo-fix/sync-price-sheets/stop'),
  sendToTg:                 (chatId, limit) => apiClient.post('/admin/photo-fix/send-to-tg', undefined, { params: { ...(chatId ? { chat_id: chatId } : {}), ...(limit ? { limit } : {}) } }),
  sendToTgStatus:           ()  => apiClient.get('/admin/photo-fix/send-to-tg/status'),
  sendToTgStop:             ()  => apiClient.post('/admin/photo-fix/send-to-tg/stop'),
  sendToTgFailed:           ()  => apiClient.get('/admin/photo-fix/send-to-tg/failed'),
  syncClothingCatalog:          ()  => apiClient.post('/admin/photo-fix/sync-clothing-catalog'),
  syncClothingCatalogStatus:    ()  => apiClient.get('/admin/photo-fix/sync-clothing-catalog/status'),
  syncClothingCatalogStop:      ()  => apiClient.post('/admin/photo-fix/sync-clothing-catalog/stop'),
  easybdprom:               (limit, start_from) => apiClient.post('/admin/photo-fix/easybdprom', null, { params: { limit, start_from } }),
  easybdpromStatus:         ()      => apiClient.get('/admin/photo-fix/easybdprom/status'),
  easybdpromStop:           ()      => apiClient.post('/admin/photo-fix/easybdprom/stop'),
  queueStart:               (tasks) => apiClient.post('/admin/photo-fix/queue/start', tasks),
  queueStatus:              ()  => apiClient.get('/admin/photo-fix/queue/status'),
  queueStop:                ()  => apiClient.post('/admin/photo-fix/queue/stop'),
  getImageCache:            (params) => apiClient.get('/admin/photo-fix/image-cache', { params }),
  upload:           (originalUrl, file) => {
    const fd = new FormData();
    fd.append('original_url', originalUrl);
    fd.append('file', file);
    return apiClient.post('/admin/photo-fix/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const automationAPI = {
  autoArticleSync:       () => apiClient.post('/admin/automation/auto-article-sync'),
  autoArticleSyncStatus: () => apiClient.get('/admin/automation/auto-article-sync/status'),
  autoArticleSyncStop:   () => apiClient.post('/admin/automation/auto-article-sync/stop'),
};

export const selectionAPI = {
  getMeta: () => apiClient.get('/admin/selection/meta'),
  send:    (payload) => apiClient.post('/admin/selection/send', payload),
  // User-facing (no admin required, uses own tg_id)
  getMetaUser: () => apiClient.get('/selection/meta-user'),
  sendMe: (payload) => apiClient.post('/selection/send-me', payload),
};

export const analyticsAPI = {
  getSummary: () => apiClient.get('/admin/analytics/summary'),
  getTopProducts: (params) => apiClient.get('/admin/analytics/top-products', { params }),
  getSalesChart: (params) => apiClient.get('/admin/analytics/sales-chart', { params }),
  getSeasonal: () => apiClient.get('/admin/analytics/seasonal'),
  getSuppliers: (params) => apiClient.get('/admin/analytics/suppliers', { params }),
  getForecast: (art, params) => apiClient.get(`/admin/analytics/forecast/${art}`, { params }),
  getPurchaseIntelligence: (params) => apiClient.get('/admin/analytics/purchase-intelligence', { params }),
  getNoveltyProducts: (params) => apiClient.get('/admin/analytics/novelty-products', { params }),
  getEvergreens: (params) => apiClient.get('/admin/analytics/evergreens', { params }),
  getSizeDistribution: (art, params) => apiClient.get(`/admin/analytics/size-distribution/${art}`, { params }),
  getHotNow: (params) => apiClient.get('/admin/analytics/hot-now', { params }),
  getConsistent: (params) => apiClient.get('/admin/analytics/consistent', { params }),
  getNoveltiesData: (params) => apiClient.get('/admin/analytics/novelties', { params }),
  getNeedRestock: (params) => apiClient.get('/admin/analytics/need-restock', { params }),
  getSupplierDetail: (supplier) => apiClient.get(`/admin/analytics/supplier-detail/${encodeURIComponent(supplier)}`),
  getSupplierSalesLog: (supplier, params) => apiClient.get(`/admin/analytics/supplier-sales-log/${encodeURIComponent(supplier)}`, { params }),
  getSizeOrder: (art, params) => apiClient.get(`/admin/analytics/size-order/${encodeURIComponent(art)}`, { params }),
  getProductReport: (art) => apiClient.get(`/admin/analytics/product-report/${encodeURIComponent(art)}`),
  getPriceHistory:  (art) => apiClient.get(`/admin/analytics/price-history/${encodeURIComponent(art)}`),
  searchProducts: (q) => apiClient.get('/admin/analytics/search-products', { params: { q } }),
  getLostOpportunities: (params) => apiClient.get('/admin/analytics/lost-opportunities', { params }),
  getCheeseStatus: (skus) => apiClient.get('/admin/analytics/cheese-status', { params: { skus: skus.join(',') } }),
  getCheesePurchaseAnalysis: (params) => apiClient.get('/admin/analytics/cheese-purchase-analysis', { params }),
};

export const clothingAPI = {
  list: (skip = 0, limit = 48, filters = {}, signal) => {
    const { sizes, ...rest } = filters;
    const params = { skip, limit, ...rest };
    if (sizes && sizes.length > 0) params.sizes = sizes.join(',');
    return apiClient.get('/clothing/products/', { params, ...(signal ? { signal } : {}) });
  },
  getBySku: (sku) => apiClient.get('/clothing/products/by-sku', { params: { sku } }),
  getBySkus: (skus) => apiClient.get('/clothing/products/by-skus', { params: { skus: skus.join(',') } }),
  getSuppliers: (sku) => apiClient.get(`/clothing/products/${encodeURIComponent(sku)}/suppliers`),
  getFilters: () => apiClient.get('/clothing/filters'),
  getSizes: (product_type) => apiClient.get('/clothing/sizes', { params: product_type ? { product_type } : {} }),
  getNav: () => apiClient.get('/clothing/nav'),
  getModels: (product_type, brand) => apiClient.get('/clothing/models', { params: { ...(product_type ? { product_type } : {}), ...(brand ? { brand } : {}) } }),
  searchNav: (q, product_type, brand) => apiClient.get('/clothing/search-nav', {
    params: { q, ...(product_type ? { product_type } : {}), ...(brand ? { brand } : {}) },
  }),
  getSyncStatus: () => apiClient.get('/clothing/sync/status'),
  getSuppliersStatus: () => apiClient.get('/clothing/sync-suppliers/status'),
  getModelsStatus: () => apiClient.get('/clothing/sync-models/status'),
  sync: () => apiClient.post('/admin/clothing/sync'),
  syncSuppliers: () => apiClient.post('/admin/clothing/sync-suppliers'),
  syncModels: () => apiClient.post('/admin/clothing/sync-models'),
  syncAll: () => apiClient.post('/admin/clothing/sync-all'),
};
export const proxyClothingImageUrl = (url) => url || '';

export const clothingDiscountsAPI = {
  getSuppliers: () =>
    apiClient.get('/clothing/discounts/suppliers'),

  list: () =>
    apiClient.get('/clothing/discounts/'),

  create: (supplier_name_norm, discount_amount) =>
    apiClient.post('/clothing/discounts/', { supplier_name_norm, discount_amount }),

  update: (id, discount_amount) =>
    apiClient.patch(`/clothing/discounts/${id}`, { discount_amount }),

  remove: (id) =>
    apiClient.delete(`/clothing/discounts/${id}`),
};

export const cheeseAPI = {
  // Фото для публікації — окремий upload, доступний усім з правом cheese
  // (не тільки повним адмінам, на відміну від adminItemsAPI.uploadPhoto)
  uploadPhoto: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post('/admin/cheese/upload-photo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Заказы (Закупка)
  listOrders:      ()        => apiClient.get('/admin/cheese/orders'),
  createOrder:     (data)    => apiClient.post('/admin/cheese/orders', data),
  updateOrder:     (id, data)=> apiClient.patch(`/admin/cheese/orders/${id}`, data),
  deleteOrder:     (id)      => apiClient.delete(`/admin/cheese/orders/${id}`),
  orderToTransit:  (id, body) => apiClient.post(`/admin/cheese/orders/${id}/to-transit`, body ?? {}),
  exportOrdersXlsx:   ()     => apiClient.get('/admin/cheese/export/orders/xlsx', { responseType: 'blob' }),
  exportOrdersGSheet: ()     => apiClient.get('/admin/cheese/export/orders/gsheet', { responseType: 'blob' }),
  sendOrdersTg:       ()     => apiClient.post('/admin/cheese/export/orders/tg'),
  resetOrderExport:   (id)   => apiClient.post(`/admin/cheese/orders/${id}/reset-export`),

  // В пути
  // Партии
  listBatches:        ()           => apiClient.get('/admin/cheese/batches'),
  createBatch:        (data)       => apiClient.post('/admin/cheese/batches', data),
  updateBatch:        (id, data)   => apiClient.patch(`/admin/cheese/batches/${id}`, data),
  deleteBatch:        (id)         => apiClient.delete(`/admin/cheese/batches/${id}`),
  assignItemsToBatch: (id, itemIds)=> apiClient.post(`/admin/cheese/batches/${id}/assign`, { item_ids: itemIds }),
  uploadBatchPhoto:   (file)       => { const fd = new FormData(); fd.append('file', file); return apiClient.post('/admin/cheese/batches/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },

  getPrevTransit:  (sku)     => apiClient.get('/admin/cheese/transit/prev', { params: { catalog_sku: sku } }),
  listTransit:     (status)  => apiClient.get('/admin/cheese/transit', { params: status ? { status } : {} }),
  getTransit:      (id)      => apiClient.get(`/admin/cheese/transit/${id}`),
  createTransit:   (data)    => apiClient.post('/admin/cheese/transit', data),
  updateTransit:   (id, data)=> apiClient.patch(`/admin/cheese/transit/${id}`, data),
  deleteTransit:   (id)      => apiClient.delete(`/admin/cheese/transit/${id}`),
  easydropCreate:  (id, fields) => apiClient.post(`/admin/cheese/transit/${id}/easydrop/create`, fields || {}),
  easydropRestock: (id)      => apiClient.post(`/admin/cheese/transit/${id}/easydrop/restock`),
  easydropPreviewCreate:  (id) => apiClient.get(`/admin/cheese/transit/${id}/easydrop/preview-create`),
  easydropPreviewRestock: (id) => apiClient.get(`/admin/cheese/transit/${id}/easydrop/preview-restock`),
  easydropMatch:   (id)      => apiClient.get(`/admin/cheese/transit/${id}/easydrop/match`),
  easydropSearch:  (q)       => apiClient.get(`/admin/cheese/easydrop/search`, { params: { q } }),
  easydropLink:    (id, easydropItemId) => apiClient.post(`/admin/cheese/transit/${id}/easydrop/link`, { easydrop_item_id: easydropItemId }),
  easydropUnlink:  (id)       => apiClient.post(`/admin/cheese/transit/${id}/easydrop/unlink`),
  easydropHistory: (id)       => apiClient.get(`/admin/cheese/transit/${id}/easydrop/history`),
  exportTransitXlsx: (status)=> apiClient.get('/admin/cheese/export/transit/xlsx', { params: status ? { status } : {}, responseType: 'blob' }),

  getCaption:      (id)      => apiClient.get(`/admin/cheese/transit/${id}/caption`),
  getSalesHistory: (art)    => apiClient.get(`/admin/cheese/art/${encodeURIComponent(art)}/sales-history`),

  // Telegram
  tgPublish:       (transit_id, custom_text, no_caption = false) => apiClient.post('/admin/cheese/telegram/publish', { transit_id, custom_text: no_caption ? undefined : custom_text, no_caption }),
  tgEdit:          (transit_id, data)        => apiClient.post('/admin/cheese/telegram/edit', { transit_id, ...data }),
  tgComment:       (transit_id, text, photo_url) => apiClient.post('/admin/cheese/telegram/comment', { transit_id, text, photo_url }),
  tgCommentFiles:  (transit_id, text, files) => {
    const fd = new FormData();
    fd.append('transit_id', transit_id);
    fd.append('text', text || '');
    files.forEach(f => fd.append('files', f));
    return apiClient.post('/admin/cheese/telegram/comment-files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Категории
  listCategories:   ()        => apiClient.get('/admin/cheese/categories'),
  createCategory:   (data)    => apiClient.post('/admin/cheese/categories', data),
  updateCategory:   (id, data)=> apiClient.patch(`/admin/cheese/categories/${id}`, data),
  deleteCategory:   (id)      => apiClient.delete(`/admin/cheese/categories/${id}`),
  getNextItemId:    ()        => apiClient.get('/admin/cheese/next-item-id'),
};

export const retailAPI = {
  // Платформы
  getPlatforms:    ()          => apiClient.get('/admin/retail/platforms'),
  // Сайты
  getSites:        ()          => apiClient.get('/admin/retail/sites'),
  getSiteErrors:   ()          => apiClient.get('/admin/retail/sites/errors'),
  createSite:      (data)      => apiClient.post('/admin/retail/sites', data),
  updateSite:         (id, data) => apiClient.patch(`/admin/retail/sites/${id}`, data),
  updateSiteDefaults: (id, data) => apiClient.patch(`/admin/retail/sites/${id}/defaults`, data),
  deleteSite:      (id)        => apiClient.delete(`/admin/retail/sites/${id}`),
  testNewSite:     (data)      => apiClient.post('/admin/retail/sites/test-new', data),
  testSite:        (id)        => apiClient.post(`/admin/retail/sites/${id}/test`),
  shopifyOAuthStart: (id)      => apiClient.post(`/admin/retail/sites/${id}/shopify/oauth/start`),
  getCategories:   (id, refresh = false) => apiClient.get(`/admin/retail/sites/${id}/categories`, refresh ? { params: { refresh: true } } : undefined),
  patchCategory:   (siteId, extId, data) => apiClient.patch(`/admin/retail/sites/${siteId}/categories/${extId}`, data),

  // Групи сайтів
  getServerStats:   ()          => apiClient.get('/admin/retail/server-stats'),
  getSiteGroups:    ()          => apiClient.get('/admin/retail/site-groups'),
  createSiteGroup:  (data)      => apiClient.post('/admin/retail/site-groups', data),
  updateSiteGroup:  (id, data)  => apiClient.patch(`/admin/retail/site-groups/${id}`, data),
  deleteSiteGroup:  (id)        => apiClient.delete(`/admin/retail/site-groups/${id}`),
  bulkGroupSyncConfig: (groupId, data) => apiClient.patch(`/admin/retail/site-groups/${groupId}/bulk-sync-config`, data),
  supplierSuggestions: (q) => apiClient.get('/admin/retail/supplier-suggestions', { params: { q } }),
  // Фото
  uploadPhoto:     (formData)  => apiClient.post('/admin/retail/upload-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Публикация
  publish:         (data)      => apiClient.post('/admin/retail/publish', data),
  generateProductName: (photo_url, hint_text) =>
    apiClient.post('/admin/retail/generate-product-name', { photo_url, hint_text }),
  getBrandPrefixes:   ()          => apiClient.get('/admin/retail/brand-prefixes'),
  createBrandPrefix:  (data)      => apiClient.post('/admin/retail/brand-prefixes', data),
  updateBrandPrefix:  (id, data)  => apiClient.put(`/admin/retail/brand-prefixes/${id}`, data),
  deleteBrandPrefix:  (id)        => apiClient.delete(`/admin/retail/brand-prefixes/${id}`),
  articlePreview:     (data)      => apiClient.post('/admin/retail/article-preview', data),
  generateProductFields: (photo_url, context, options, sites = [], unisex_titles = false) =>
    apiClient.post('/admin/retail/generate-product-fields', { photo_url, context, options, sites, unisex_titles }),

  // Товары сайта
  syncProducts:    (id, resume = false) => apiClient.post(`/admin/retail/sites/${id}/sync-products${resume ? '?resume=true' : ''}`),
  syncProductsFull: (id) => apiClient.post(`/admin/retail/sites/${id}/sync-products?force_full=true`),
  importAllNoGql:  (id) => apiClient.post(`/admin/retail/sites/${id}/import-all`),
  fillPromSizes:   (id) => apiClient.post(`/admin/retail/sites/${id}/fill-sizes`),
  recheckDuplicateSizes: (id) => apiClient.post(`/admin/retail/sites/${id}/recheck-duplicate-sizes`),
  getSyncStatus:   (id)        => apiClient.get(`/admin/retail/sites/${id}/sync-status`),
  getSiteProducts: (id, params) => apiClient.get(`/admin/retail/sites/${id}/products`, { params }),
  updateSiteProduct: (siteId, productId, data) =>
    apiClient.patch(`/admin/retail/sites/${siteId}/products/${productId}`, data),
  fixProductBinding: (siteId, productId, allSku) =>
    apiClient.patch(`/admin/retail/sites/${siteId}/products/${productId}/binding`, { all_sku: allSku }),
  bulkLink: (siteId, text) =>
    apiClient.post(`/admin/retail/sites/${siteId}/bulk-link`, text, { headers: { 'Content-Type': 'text/plain' } }),
  getStockLog: (siteId, limit = 200, q = '') =>
    apiClient.get(`/admin/retail/sites/${siteId}/stock-log`, { params: { limit, q } }),
  getSiteProductVariants: (siteId, productId, params = {}) =>
    apiClient.get(`/admin/retail/sites/${siteId}/products/${productId}/variants`, { params }),
  pushProductToSite: (siteId, productId) =>
    apiClient.post(`/admin/retail/sites/${siteId}/products/${productId}/push-to-site`),

  // Анализ
  getMissingProducts: (id, params) =>
    apiClient.get(`/admin/retail/sites/${id}/missing-products`, { params }),
  getMissingProductsOptions: (id, params) =>
    apiClient.get(`/admin/retail/sites/${id}/missing-products/options`, { params }),
  getMissingProductAddTargets: (id, sku) =>
    apiClient.get(`/admin/retail/sites/${id}/missing-products/${encodeURIComponent(sku)}/add-targets`),
  getCategoryLinkSuggest: (siteId, categoryId, targetSiteIds) =>
    apiClient.get('/admin/retail/category-link-suggest', {
      params: { site_id: siteId, category_id: categoryId, target_site_ids: targetSiteIds.join(',') },
    }),
  getMissingSizes: (id, params) =>
    apiClient.get(`/admin/retail/sites/${id}/missing-sizes`, { params }),
  addMissingSizes: (id, spId, sizes) =>
    apiClient.post(`/admin/retail/sites/${id}/products/${spId}/add-size`, { sizes }),

  getEmptyStock: (id, params) =>
    apiClient.get(`/admin/retail/sites/${id}/empty-stock`, { params }),

  getBrokenBindings: (id, params) =>
    apiClient.get(`/admin/retail/sites/${id}/broken-bindings`, { params }),

  // Обновление наличия/цен (только ручное!) — запускается в фоне, статус через getUpdateStockStatus
  updateStock:     (id, force = false) => apiClient.post(`/admin/retail/sites/${id}/update-stock${force ? '?force=true' : ''}`),
  getUpdateStockStatus: (id) => apiClient.get(`/admin/retail/sites/${id}/update-stock-status`),

  // Конфиг синхронизации
  getSyncConfig:   (id)        => apiClient.get(`/admin/retail/sites/${id}/sync-config`),
  updateSyncConfig: (id, data) => apiClient.patch(`/admin/retail/sites/${id}/sync-config`, data),

  // Список постачальників для сайту (для фільтра виключення)
  getSiteSuppliers: (id) => apiClient.get(`/admin/retail/sites/${id}/suppliers`),

  // Об'єднати "Правила постачальників" цього сайту з іншими в спільну групу (тільки доступні)
  shareSupplierRules: (id, targetSiteIds) => apiClient.post(`/admin/retail/sites/${id}/supplier-rules/share`, { target_site_ids: targetSiteIds }),
  // Відв'язати сайт від спільної групи правил постачальників
  unshareSupplierRules: (id) => apiClient.post(`/admin/retail/sites/${id}/supplier-rules/unshare`),

  // Prom.ua: масова зміна наявності (лише адмін)
  bulkSetPresence: (id, presence, only_in_stock = false) => apiClient.post(`/admin/retail/sites/${id}/bulk-set-presence`, { presence, only_in_stock }),

  // Очистити всі товари сайту і скинути лічильники синку (лише адмін)
  clearProducts: (id) => apiClient.post(`/admin/retail/sites/${id}/clear-products`),

  // Prom: список "основний під заміну"
  getSubstitutionList:  (id) => apiClient.get(`/admin/retail/sites/${id}/substitution-list`),
  getSuspiciousSizes:   (id, params = {}) => apiClient.get(`/admin/retail/sites/${id}/suspicious-sizes`, { params }),

  // Персистентні прив'язки article_site → all_sku
  getMappings:    (id)           => apiClient.get(`/admin/retail/sites/${id}/mappings`),
  deleteMapping:  (id, mapId)    => apiClient.delete(`/admin/retail/sites/${id}/mappings/${mapId}`),

  // Прямі прив'язки: товар постачальника (без ALL) → конкретний артикул сайту
  searchSupplierItems: (q)              => apiClient.get(`/admin/retail/supplier-items/search`, { params: { q } }),
  checkArticleExists:  (id, article)    => apiClient.get(`/admin/retail/sites/${id}/article-exists`, { params: { article } }),
  getDirectLinks:      (id)             => apiClient.get(`/admin/retail/sites/${id}/direct-links`),
  createDirectLink:    (id, data)       => apiClient.post(`/admin/retail/sites/${id}/direct-links`, data),
  deleteDirectLink:    (id, linkId)     => apiClient.delete(`/admin/retail/sites/${id}/direct-links/${linkId}`),

  // Справочники характеристик
  getCharValues:       (id, refresh = false) => apiClient.get(`/admin/retail/sites/${id}/char-values${refresh ? '?refresh=true' : ''}`),
  autoSizeMap:         (id, charKey) => apiClient.get(`/admin/retail/sites/${id}/auto-size-map`, { params: { char_key: charKey } }),
  getCharValuesGlobal: ()      => apiClient.get('/admin/retail/char-values'),

  // Чёрный список «Не додавати»
  addExclude:    (data)  => apiClient.post('/admin/retail/excludes', data),
  removeExclude: (sku)   => apiClient.delete(`/admin/retail/excludes/${encodeURIComponent(sku)}`),
  listExcludes:  ()      => apiClient.get('/admin/retail/excludes'),

  // Предпросмотр обновления наличия (без отправки)
  getPendingUpdates: (id) => apiClient.get(`/admin/retail/sites/${id}/pending-updates`),
  updateStockOne:   (siteId, siteProductId, manualPrice) =>
    apiClient.post(`/admin/retail/sites/${siteId}/update-stock-one/${siteProductId}${manualPrice != null ? `?manual_price=${manualPrice}` : ''}`),
  updateProductField: (siteId, productId, fields) =>
    apiClient.patch(`/admin/retail/sites/${siteId}/products/${productId}`, fields),
  testUpdateProduct:(siteId, body)          => apiClient.post(`/admin/retail/sites/${siteId}/test-update`, body),
  bulkSetOldPrice:  (siteId, clear = false) => apiClient.post(`/admin/retail/sites/${siteId}/bulk-set-old-price`, null, { params: { clear } }),

  // Управление синхронизацией
  stopSync: (id) => apiClient.post(`/admin/retail/sites/${id}/sync-stop`),

  // Доступ конкретного користувача до сайтів (з боку юзера)
  getUserSiteAccess: (userId)          => apiClient.get(`/admin/retail/users/${userId}/sites`),
  setUserSiteAccess: (userId, siteIds) => apiClient.put(`/admin/retail/users/${userId}/sites`, { site_ids: siteIds }),

  // Доступ до сайту (з боку сайту)
  getSiteUsers:      (siteId)          => apiClient.get(`/admin/retail/sites/${siteId}/users`),
  grantSiteAccess:   (siteId, userId)  => apiClient.post(`/admin/retail/sites/${siteId}/users/${userId}`),
  revokeSiteAccess:  (siteId, userId)  => apiClient.delete(`/admin/retail/sites/${siteId}/users/${userId}`),
};

export default apiClient;
